// pages/DwiSendShop.jsx
// The DwiSend shop for DRC clients (USD), in French or English — the Boutique
// and Mes commandes tabs of /dwisend-drc (old /dwisend-shop links forward there)
// (frontend/src/i18n/dwisend.js). Copy of Uganda's shop flow, paid offline:
//   pick products + sizes → basket → order (stock held 48 h) → pay with the
//   order reference (details shown on the order) → two DwiSend people check the
//   money → paid → on its way → delivered.
// The server decides who may use it (DRC account + dwisend_shop switch) and
// re-checks every price, stock level and fee.
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { ZoomIn } from 'lucide-react';
import { dwisendShopAPI, dwisendRequestAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Button, Card, Input, Select, Badge, Empty, Spinner } from '../components/ui';
import { t, money, date, dateTime, langFor, LANGS } from '../i18n/dwisend';
import { QtyStepper, FloatingCart, CartDrawer, isDesktop, useSavedCart, refreshCart } from '../components/shop/CartControls';
import { PhotoViewer } from '../components/shop/PhotoViewer';

const STATUS_COLOR = { awaiting_payment: 'amber', paid: 'teal', dispatched: 'cyan', delivered: 'green', cancelled: 'gray', expired: 'gray' };
const textarea = 'mt-1 w-full bg-bg-elevated border border-border rounded-xl px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan';

// embedded: shown as the Boutique / Mes commandes tabs of the DwiSend page
// (pages/DwiSendHub.jsx), which owns the title, language and tab row.
export default function DwiSendShop({ embedded = false, lang: hubLang, tab: hubTab, onTab, onUnpaid } = {}) {
  const { user, updateUser } = useAuth();
  const [ownLang, setLang]  = useState(() => langFor(user));
  const lang = hubLang || ownLang;
  const [data, setData]     = useState(null);
  const [closed, setClosed] = useState(false);
  const [ownTab, setOwnTab] = useState('shop');
  const tab = hubTab || ownTab;
  const setTab = onTab || setOwnTab;
  // [{ productId, variantId, qty, priceMinor (last seen) }] — kept in this browser for this account
  const [basket, setBasket] = useSavedCart(user ? `drc:${user._id || user.id}` : null);
  const T = (k, v) => t(lang, k, v);

  const load = useCallback(async () => {
    try { setData(await dwisendShopAPI.mine()); setClosed(false); }
    catch (err) { if (err.status === 403) setClosed(true); else toast.error(err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (data) onUnpaid?.(data.orders.filter(o => o.status === 'awaiting_payment').length); }, [data, onUnpaid]);

  // A saved basket may be from an earlier visit: re-check it against the shop
  // each time it loads (current price, stock, still on sale) and say what changed.
  const checkedFor = useRef(null);
  useEffect(() => {
    if (!data || checkedFor.current === data) return;
    checkedFor.current = data;
    const r = refreshCart(basket, data.products, 'priceMinor');
    if (r.same) return;
    setBasket(r.lines.map(({ productId, variantId, qty, priceMinor }) => ({ productId, variantId, qty, priceMinor })));
    if (r.changed) {
      const parts = [
        r.repriced.length && t(lang, 'shop.cart.repriced', { names: r.repriced.join(', ') }),
        r.reduced.length && t(lang, 'shop.cart.reduced', { names: r.reduced.join(', ') }),
        r.removed.length && t(lang, 'shop.cart.removed', { names: r.removed.filter(Boolean).join(', ') || '—' }),
      ].filter(Boolean);
      toast(`${t(lang, 'shop.cart.updated')} ${parts.join(' ; ')}. ${t(lang, 'shop.cart.check')}`, { icon: '🛒', duration: 7000 });
    }
  }, [data, basket, setBasket, lang]);

  const switchLang = l => {
    setLang(l);
    updateUser({ language: l });
    dwisendRequestAPI.setLanguage(l).catch(() => {});
  };

  // Never more in the basket than "X available" (the server re-checks stock on order).
  const add = (productId, variantId, qty, max, priceMinor) => {
    const have = basket.find(x => x.productId === productId && x.variantId === variantId)?.qty || 0;
    const n = Math.min(qty, max - have);
    if (n < 1) return toast.error(T('shop.only_available', { n: max }));
    setBasket(b => {
      const i = b.findIndex(x => x.productId === productId && x.variantId === variantId);
      if (i < 0) return [...b, { productId, variantId, qty: n, priceMinor }];
      return b.map((x, j) => (j === i ? { ...x, qty: x.qty + n, priceMinor } : x));
    });
    toast.success(n < qty ? `${T('shop.added')} — ${T('shop.only_available_short', { n: max })}` : T('shop.added'));
  };

  const header = embedded ? null : (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h1 className="text-xl font-black text-text-primary">{T('shop.title')}</h1>
        <p className="text-sm text-text-secondary mt-1 max-w-2xl">{T('shop.subtitle')}</p>
      </div>
      <div className="flex items-center gap-1 text-xs" aria-label={T('req.lang')}>
        {LANGS.map(l => (
          <button key={l} onClick={() => switchLang(l)}
            className={`px-2.5 py-1 rounded-lg font-bold ${lang === l ? 'bg-cyan/20 text-cyan' : 'text-text-muted hover:text-text-primary'}`}>
            {l === 'fr' ? 'Français' : 'English'}
          </button>
        ))}
      </div>
    </div>
  );

  if (closed) return <div className="space-y-4">{header}<Card><Empty icon="🛒" title={T('shop.closed.title')} description={T('shop.closed.desc')} /></Card></div>;
  if (!data) return <div className="py-16 flex justify-center"><Spinner /></div>;

  const unpaid = data.orders.filter(o => o.status === 'awaiting_payment').length;
  return (
    <div className="space-y-4">
      {header}
      {!embedded && (
        <div className="flex gap-2">
          {[['shop', T('shop.tab.shop')], ['orders', `${T('shop.tab.orders')}${unpaid ? ` (${unpaid})` : ''}`]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${tab === k ? 'bg-cyan/20 text-cyan' : 'text-text-muted hover:text-text-primary'}`}>{label}</button>
          ))}
        </div>
      )}
      {tab === 'shop'
        ? <ShopTab lang={lang} data={data} basket={basket} setBasket={setBasket} onAdd={add} onFresh={setData}
            onOrdered={() => { setBasket([]); setTab('orders'); load(); }} />
        : <OrdersTab lang={lang} data={data} onChanged={load} />}
    </div>
  );
}

function ShopTab({ lang, data, basket, setBasket, onAdd, onOrdered, onFresh }) {
  const T = (k, v) => t(lang, k, v);
  const [cat, setCat] = useState('');
  const shown = data.products.filter(p => !cat || p.categoryId === cat);
  if (!data.products.length) return <Card><Empty icon="🛒" title={T('shop.empty.title')} description={T('shop.empty.desc')} /></Card>;
  return (
    <div className="grid lg:grid-cols-3 gap-4 items-start">
      <div className="lg:col-span-2 space-y-3">
        {data.categories.length > 1 && (
          <div className="flex gap-2 flex-wrap">
            {[{ _id: '', name: T('shop.all') }, ...data.categories].map(c => (
              <button key={c._id} onClick={() => setCat(c._id)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border ${cat === c._id ? 'border-cyan text-cyan' : 'border-border text-text-muted'}`}>{c.name}</button>
            ))}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          {shown.map(p => <ProductCard key={p._id} p={p} lang={lang} onAdd={onAdd} />)}
        </div>
      </div>
      <Basket lang={lang} data={data} basket={basket} setBasket={setBasket} onOrdered={onOrdered} onFresh={onFresh} />
    </div>
  );
}

export function ProductCard({ p, lang, onAdd }) {
  const T = (k, v) => t(lang, k, v);
  const [variantId, setVariantId] = useState(p.variants[0]?._id || '');
  const v = p.variants.find(x => x._id === variantId) || p.variants[0];
  const [qty, setQty] = useState(String(v?.minOrderQty || 1));
  const [viewing, setViewing] = useState(false);   // full-screen photo viewer
  const d = p.details || {};
  const facts = [d.brand && `${T('shop.brand')} : ${d.brand}`, d.origin && `${T('shop.origin')} : ${d.origin}`,
    d.warranty && `${T('shop.warranty')} : ${d.warranty}`, d.condition && T(`shop.condition.${d.condition}`)].filter(Boolean);
  const q = Number(qty);
  const ok = v && Number.isInteger(q) && q >= (v.minOrderQty || 1) && q <= v.inStock;
  const pickSize = id => { setVariantId(id); const nv = p.variants.find(x => x._id === id); setQty(String(nv?.minOrderQty || 1)); };
  return (
    <Card>
      {p.photos[0] && (
        <button type="button" onClick={() => setViewing(true)} aria-label={T('shop.photo.enlarge', { name: p.name })} className="relative block w-full mb-2 cursor-zoom-in">
          <img src={p.photos[0]} alt={p.name} className="w-full h-40 object-cover rounded-xl" loading="lazy" />
          <span className="absolute bottom-1.5 right-1.5 h-7 min-w-[1.75rem] px-1.5 gap-1 rounded-full bg-black/60 text-white text-[11px] font-semibold flex items-center justify-center pointer-events-none">
            <ZoomIn size={15} />{p.photos.length > 1 && <span className="tabular-nums">{p.photos.length}</span>}
          </span>
        </button>
      )}
      {viewing && <PhotoViewer photos={p.photos} alt={p.name} onClose={() => setViewing(false)}
        labels={{ close: T('shop.close'), prev: T('shop.photo.prev'), next: T('shop.photo.next'), zoomIn: T('shop.photo.zoom_in'), zoomOut: T('shop.photo.zoom_out') }} />}
      <div className="text-text-primary font-bold">{p.name}</div>
      <div className="text-[11px] text-text-muted">{p.categoryName}{facts.length ? ` · ${facts.join(' · ')}` : ''}</div>
      {p.description && <div className="text-xs text-text-secondary mt-1 whitespace-pre-line">{p.description}</div>}
      <div className="grid grid-cols-2 gap-2 mt-3 items-end">
        <Select label={T('shop.size')} value={v?._id || ''} onChange={e => pickSize(e.target.value)}>
          {p.variants.map(x => <option key={x._id} value={x._id}>{x.unitName} — {money(x.priceMinor, p.currency, lang)}</option>)}
        </Select>
        <Input label={T('shop.qty')} type="number" min={v?.minOrderQty || 1} step="1" value={qty} onChange={e => setQty(e.target.value)} />
      </div>
      <div className="flex items-center justify-between gap-2 mt-2">
        <span className="text-[11px] text-text-muted">
          {v?.inStock > 0 ? T('shop.in_stock', { n: v.inStock }) : T('shop.out_of_stock')}{v?.minOrderQty > 1 ? ` · ${T('shop.min', { n: v.minOrderQty })}` : ''}
        </span>
        <Button size="sm" disabled={!ok} onClick={() => onAdd(p._id, v._id, q, v.inStock, v.priceMinor)}>{T('shop.add')}</Button>
      </div>
    </Card>
  );
}

function Basket({ lang, data, basket, setBasket, onOrdered, onFresh }) {
  const { user } = useAuth();
  const T = (k, v) => t(lang, k, v);
  const [f, setF] = useState({ cityId: '', deliveryAddress: '', phone: user?.phone || '', whatsapp: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [open, setOpen]     = useState(false);   // phone: the basket slide-over
  const [flash, setFlash]   = useState(false);   // computer: highlight the side column
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  const set = k => e => setF(p => ({ ...p, [k]: e.target.value }));
  const m = minor => money(minor, data.currency, lang);
  const setLineQty = (l, qty) => setBasket(b => b.map(x => (x.productId === l.productId && x.variantId === l.variantId
    ? { ...x, qty: Math.max(l.min, Math.min(l.max, qty)) } : x)));
  const openBasket = () => {
    if (!isDesktop()) return setOpen(true);
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setFlash(true);
    setTimeout(() => setFlash(false), 900);
  };

  // Preview only — the server re-prices every line and the fee.
  const lines = useMemo(() => basket.map(b => {
    const p = data.products.find(x => x._id === b.productId);
    const v = p?.variants.find(x => x._id === b.variantId);
    return p && v ? { ...b, name: p.name, unitName: v.unitName, priceMinor: v.priceMinor, min: v.minOrderQty || 1, max: v.inStock, total: v.priceMinor * b.qty } : null;
  }).filter(Boolean), [basket, data.products]);
  const subtotal = lines.reduce((s, l) => s + l.total, 0);
  // Delivery is set per order after it is placed (DwiSend weighs the package) — the basket shows products only.

  const send = async () => {
    setSaving(true);
    try {
      // Last look at the shop: if a price or stock level changed since this
      // page loaded, show the client the new basket instead of ordering.
      const fresh = await dwisendShopAPI.mine();
      if (refreshCart(basket, fresh.products, 'priceMinor').changed) {
        onFresh(fresh);
        return;
      }
      await dwisendShopAPI.order({ ...f, items: lines.map(l => ({ productId: l.productId, variantId: l.variantId, qty: l.qty })) });
      toast.success(T('shop.checkout.done'));
      setOpen(false);
      onOrdered();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <>
    <CartDrawer open={open} onClose={close} closeLabel={T('shop.close')} panelRef={ref}>
    <Card className={flash ? 'ring-2 ring-cyan transition' : 'transition'}>
      <div className="text-text-primary font-bold mb-2">{T('shop.basket')}</div>
      {!lines.length ? <div className="text-sm text-text-muted">{T('shop.basket.empty')}</div> : (
        <div className="space-y-3">
          <div className="space-y-3 text-sm">
            {lines.map(l => (
              <div key={`${l.productId}:${l.variantId}`} className="space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-text-primary truncate">{l.name}</div>
                    <div className="text-[11px] text-text-muted">{l.unitName} · {T('shop.each', { price: m(l.priceMinor) })}</div>
                  </div>
                  <button className="text-[11px] text-red underline shrink-0"
                    onClick={() => setBasket(b => b.filter(x => !(x.productId === l.productId && x.variantId === l.variantId)))}>{T('shop.basket.remove')}</button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <QtyStepper value={l.qty} min={l.min} max={l.max} onChange={n => setLineQty(l, n)} lessLabel={T('shop.qty.less')} moreLabel={T('shop.qty.more')} />
                  <span className="font-semibold text-text-primary tabular-nums">{m(l.total)}</span>
                </div>
                {l.qty >= l.max && <div className="text-[11px] text-amber">{T('shop.max_in_basket', { n: l.max })}</div>}
              </div>
            ))}
          </div>
          {data.minOrderMinor > 0 && <div className="text-[11px] text-text-muted">{T('shop.min_order', { amount: m(data.minOrderMinor) })}</div>}
          <div className="text-sm font-bold text-text-primary border-t border-border pt-2">{T('shop.checkout.title')}</div>
          {!data.cities.length ? <div className="text-sm text-text-muted">{T('shop.checkout.no_city')}</div> : (
            <>
              <Select label={T('req.form.city')} value={f.cityId} onChange={set('cityId')}>
                <option value="">{T('req.form.city.choose')}</option>
                {data.cities.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </Select>
              <div>
                <label className="text-xs font-semibold text-text-secondary">{T('req.form.address')}</label>
                <textarea rows={2} maxLength={500} value={f.deliveryAddress} onChange={set('deliveryAddress')} placeholder={T('req.form.address.ph')} className={textarea} />
              </div>
              <Input label={T('req.form.phone')} value={f.phone} onChange={set('phone')} placeholder="+243 8X XXX XXXX" />
              <Input label={T('req.form.whatsapp')} value={f.whatsapp} onChange={set('whatsapp')} placeholder="243XXXXXXXXX" />
              <Input label={T('req.form.notes')} value={f.notes} onChange={set('notes')} />
              <div className="text-sm space-y-0.5">
                <div className="flex justify-between"><span>{T('shop.subtotal')}</span><span>{m(subtotal)}</span></div>
                <div className="flex justify-between text-text-muted"><span>{T('shop.delivery')}</span><span className="text-amber font-semibold">{T('shop.delivery.tbc')}</span></div>
                <div className="flex justify-between font-bold text-text-primary"><span>{T('shop.total')}</span><span>{T('shop.total.plus_delivery', { amount: m(subtotal) })}</span></div>
              </div>
              <div className="text-[11px] text-text-muted">{T('shop.checkout.fee_note')} {T('shop.checkout.hold', { hours: data.paymentHoldHours })}</div>
              <Button className="w-full" loading={saving} disabled={!f.cityId} onClick={send}>{T('shop.checkout.confirm')}</Button>
            </>
          )}
        </div>
      )}
    </Card>
    </CartDrawer>
    <FloatingCart count={lines.length} total={m(subtotal)} label={T('shop.basket.button')} onClick={openBasket} />
    </>
  );
}

function OrdersTab({ lang, data, onChanged }) {
  const T = (k, v) => t(lang, k, v);
  if (!data.orders.length) return <Card><Empty icon="🧾" title={T('shop.orders.empty')} /></Card>;
  return <div className="space-y-3">{data.orders.map(o => <OrderCard key={o._id} o={o} lang={lang} whatsapp={data.whatsapp} onChanged={onChanged} />)}</div>;
}

export function OrderCard({ o, lang, whatsapp, onChanged }) {
  const T = (k, v) => t(lang, k, v);
  const [busy, setBusy] = useState(false);
  const m = minor => money(minor, o.currency, lang);
  const cancel = async () => {
    if (!window.confirm(T('shop.order.cancel.confirm', { ref: o.ref }))) return;
    setBusy(true);
    try { await dwisendShopAPI.cancel(o._id); toast.success(T('shop.order.cancelled')); onChanged(); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  return (
    <Card>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="text-text-primary font-bold">{T('shop.order.title', { ref: o.ref })}</div>
          <div className="text-[11px] text-text-muted">{T('shop.order.placed', { date: date(o.createdAt, lang) })} · {o.cityName}</div>
        </div>
        <Badge label={T(`shop.status.${o.status}`)} color={STATUS_COLOR[o.status] || 'gray'} />
      </div>
      <div className="mt-2 space-y-0.5 text-sm">
        {o.items.map((i, k) => <div key={k} className="flex justify-between gap-3"><span>{i.qty} × {i.name} ({i.unitName})</span><span>{m(i.lineTotalMinor)}</span></div>)}
        <div className="flex justify-between gap-3 text-text-muted"><span>{T('shop.delivery')}</span><span className={o.deliveryFeePending ? 'text-amber font-semibold' : ''}>{o.deliveryFeePending ? T('shop.delivery.tbc') : m(o.deliveryFeeMinor)}</span></div>
        <div className="flex justify-between gap-3 font-bold text-text-primary border-t border-border pt-1"><span>{T('shop.total')}</span><span>{o.deliveryFeePending ? T('shop.total.plus_delivery', { amount: m(o.subtotalMinor) }) : m(o.totalMinor)}</span></div>
      </div>
      {o.status === 'awaiting_payment' && o.deliveryFeePending && (
        <div className="mt-3 rounded-xl border border-cyan/40 bg-cyan/5 p-3 text-sm space-y-1">
          <div>{T('shop.order.fee_pending')}</div>
          <div className="text-[11px] text-text-muted">{T('shop.order.held_until', { date: dateTime(o.payBy, lang) })}</div>
        </div>
      )}
      {o.status === 'cancelled' && o.cancelReason && (
        <div className="text-sm mt-2 rounded-lg bg-bg-elevated px-3 py-2"><span className="text-text-muted">{T('shop.order.cancel_reason')}</span> {o.cancelReason}</div>
      )}
      {o.payment && <PaymentBox o={o} lang={lang} />}
      {o.paymentStage === 'confirmed' && <div className="text-sm text-green mt-2">✓ {T('req.pay.confirmed')}</div>}
      {o.driver?.name && <div className="text-xs text-text-secondary mt-1">{T('shop.order.driver', { name: [o.driver.name, o.driver.phone, o.driver.vehicle].filter(Boolean).join(' · ') })}</div>}
      <div className="flex gap-2 flex-wrap mt-3">
        {o.canCancel && <Button size="xs" variant="ghost" loading={busy} onClick={cancel}>{T('shop.order.cancel')}</Button>}
        {whatsapp && (
          <a href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(T('shop.order.whatsapp.msg', { ref: o.ref }))}`} target="_blank" rel="noopener noreferrer">
            <Button size="xs" variant="success">{T('req.card.whatsapp')}</Button>
          </a>
        )}
      </div>
    </Card>
  );
}

function PaymentBox({ o, lang }) {
  const T = (k, v) => t(lang, k, v);
  const p = o.payment;
  if (o.paymentStage === 'checking') return <div className="mt-3 rounded-xl border border-cyan/40 bg-cyan/5 p-3 text-sm">{T('req.pay.checking')}</div>;
  const payBy = new Date(o.payBy).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
  return (
    <div className="mt-3 rounded-xl border border-amber/50 bg-amber/5 p-3 space-y-2">
      <div className="text-sm font-bold text-text-primary">{T('req.pay.title')}</div>
      <div className="grid sm:grid-cols-2 gap-2 text-sm">
        <div><div className="text-[11px] text-text-muted">{T('req.pay.amount')}</div><div className="font-bold text-text-primary">{money(p.amountMinor, p.currency, lang)}</div></div>
        <div><div className="text-[11px] text-text-muted">{T('req.pay.reference')}</div><div className="font-mono font-bold text-cyan">{p.reference}</div></div>
      </div>
      <div className="text-sm font-semibold text-amber">{T('shop.order.pay_by', { date: payBy })}</div>
      {p.instructions.length
        ? <ul className="text-sm space-y-1">{p.instructions.map((line, i) => <li key={i} className="rounded-lg bg-bg-elevated px-3 py-1.5 select-all">{line}</li>)}</ul>
        : <div className="text-sm text-text-secondary">{T('req.pay.no_details')}</div>}
      <div className="text-xs text-text-muted">{T('shop.pay.waiting_note')}</div>
    </div>
  );
}
